import type { ReactElement, ReactNode } from 'react';
import { formatReadableDateFull, formatReadableDayTime, formatReadableTime, safeParseISO } from 'shared/util/datetime-fns';
import { formatDuration } from 'shared/util/formatting';

export type HotelResultItemFlightSummrayProps = {
    originAirport: string;
    destinationAirport: string;
    departureTime: string;
    arrivalTime?: string;
    flightDuration?: number;
    numberOfStopovers?: number;
    outbound?: boolean;
};

export const HotelResultItemFlightSummary = ({
    originAirport,
    destinationAirport,
    departureTime,
    arrivalTime,
    flightDuration,
    numberOfStopovers,
    outbound = false,
}: HotelResultItemFlightSummrayProps) => {
    const parsedDepartureTime = safeParseISO(departureTime);
    const parsedArrivalTime = safeParseISO(arrivalTime);

    const formatStopovers = (stopovers: number): string => (stopovers > 1 ? `${stopovers} mellomlandinger` : `${stopovers} mellomlanding`);

    const renderStopovers = (): ReactNode =>
        numberOfStopovers && numberOfStopovers > 0 ? (
            <div className="grid__unit u-size1of1 flex flex__column">
                <hr className="line--horizontal-line u-size1of1" />
                <div className="grid" data-testid="hotelFlight-layovers">
                    <span className="u-float-left vertical-stopover-dot">·</span>
                    <div className="u-float-right u-r-size3of4 u-truncate" data-testid="numberOfLayovers">
                        {formatStopovers(numberOfStopovers)}
                    </div>
                </div>

                <hr className="line--horizontal-line u-size1of1" />
            </div>
        ) : null;

    const renderFlight = (): ReactElement => (
        <div className="u-pb32">
            <div className="u-pb16">
                <div className={`icon-pre ${outbound ? 'icon--flight-departure' : 'icon--flight-arrival'} u-float-left u-pr8`} />
                <div className="u-licorice u-caption u-strong u-pt2" data-testid="flightDirection">
                    {outbound ? 'Utreise' : 'Hjemreise'}: {formatReadableDateFull(parsedDepartureTime)}
                </div>
            </div>
            <div className="line--border-left u-pl16 u-m-pr16">
                <div className="flex flex__row flex__align_center" data-testid="generatedFlightSummary">
                    <div className="grid grid__unit u-size4of5">
                        <div className="grid__unit u-licorice u-strong u-size1of1 u-hide-gt768">
                            kl. {formatReadableTime(parsedDepartureTime)}{' '}
                            {parsedArrivalTime ? `- ${formatReadableTime(parsedArrivalTime)}` : ''}
                        </div>
                        <div className="grid__unit u-licorice u-caption u-strong u-r-size1of4 u-hide-lt768" data-testid="departureDate">
                            {formatReadableDayTime(parsedDepartureTime)}
                        </div>
                        <div className="grid__unit u-r-size3of4" data-testid="flightOrigin">
                            {originAirport}
                        </div>
                        {renderStopovers()}
                        <div className="grid__unit u-licorice u-caption u-strong u-r-size1of4 u-hide-lt768" data-testid="arrivalDate">
                            {parsedArrivalTime && formatReadableDayTime(parsedArrivalTime)}&nbsp;
                        </div>
                        <div className="grid__unit u-r-size3of4" data-testid="flightDestination">
                            {destinationAirport}
                        </div>
                    </div>
                    <div className={`u-size1of5 ${flightDuration ? 'u-strong' : ''} u-text-right u-pl16`} data-testid="flightDuration">
                        {flightDuration ? formatDuration(flightDuration) : 'Ukjent flytid'}
                    </div>
                </div>
            </div>
        </div>
    );

    return parsedDepartureTime ? renderFlight() : null;
};
